import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Paper,
} from '@material-ui/core';
import { FiClock, FiSave, FiFileText } from 'react-icons/fi';
import DataGrid, {
  Column,
  Editing,
  Paging,
  RangeRule,
} from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import { getWeek, getWeekYear } from '../../utils/IntegrationDates';
import ProfessionaLookup from '../../components/ProfessionalLookup/index.tsx';
import WeekHeader from './WeekHeader.tsx';
import DialogResource from './DialogResource';

import api from '../../services/api';
import { useAuth } from '../../hooks/auth';
import GridLoaderNew from './GridLoaderNew';
import { errorMessage$, successMessage$ } from '../../utils/subjects';
import schedulingGroups from '../../config/schedulingGroups';
import { isRestrictedTo } from '../../utils/access';

export default function Grid({
  onEditProject,
  projectStatus: _projectStatus,
  onUpdatedMargin,
  onUpdatingMargin,
  projectComanagement: _projectComanagement,
  projectSupportComanagement: _projectSupportComanagement,
  officeId: _officeId,
  portalId,
  showDeallocated,
  setShowDeallocated,
  onReload,
  type,
  refreshCount,
  projectCode,
  projectName,
  projectProfessionalManager,
  projectProfessionalPartner,
}) {
  const getCurrentWeek = useCallback(() => {
    const currentWeekNumber = getWeek(new Date());
    const currentWeekYear = getWeekYear(new Date());
    return { WeekNumber: currentWeekNumber, Year: currentWeekYear };
  }, []);

  const gridRef = useRef();
  const history = useHistory();
  const { projectId } = useParams();

  const { user } = useAuth();
  const [userScopes] = useState(() => (user && user.scopes) || []);
  const [allowEditScopes] = useState([
    schedulingGroups.CoManager,
    schedulingGroups.Allocation,
    schedulingGroups.Admin,
    schedulingGroups.TechLab,
  ]);

  const [projectStatus] = useState(() => _projectStatus.toString());
  const [currentWeek] = useState(getCurrentWeek().WeekNumber);
  const [currentYear] = useState(getCurrentWeek().Year);
  const [closureId, setClosureId] = useState('');
  const [disabledRows, setDisabledRows] = useState([]);
  const [allowEdit, setAllowEdit] = useState(true);
  const [allowChangeStatus, setAllowChangeStatus] = useState(() =>
    userScopes.some(scope => allowEditScopes.includes(scope)),
  );
  const [professionalId, setProfessionalId] = useState(null);
  const [jobTitleId, setJobTitleId] = useState(null);
  const [allowDelete, setAllowDelete] = useState(true);
  const [professionalAllocationId, setProfessionalAllocationId] =
    useState(null);
  const [totalConflicts, setTotalConflicts] = useState(0);
  const [conflictCells, setConflictCells] = useState([]);
  const [modifiedCells, setModifiedCells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weeks, setWeeks] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [visibleProfessionals, setVisibleProfessionals] = useState([]);
  const [allProfessionals, setAllProfessionals] = useState([]);
  const [isTBD, setIsTBD] = useState();
  const [tooltip, setTooltip] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState({});
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [nextLocation, setNextLocation] = useState(null);
  const [communicationDialogOpen, setCommunicationDialogOpen] = useState(false);

  const unblockRef = useRef(null);

  const groupedUnsavedChanges = useMemo(() => {
    const groups = {};
    Object.entries(pendingUpdates).forEach(([profAllocId, changes]) => {
      const profData = allProfessionals.find(
        prof => String(prof.ID) === String(profAllocId),
      );
      const profName = profData?.Professional?.Name || 'Unknown Professional';
      const weekCaptions = [];
      Object.entries(changes).forEach(([weekKey, newValue]) => {
        const weekData = weeks.find(w => w.ID === weekKey);
        const weekCaption = weekData?.caption || weekKey;
        weekCaptions.push(weekCaption);
      });
      if (groups[profAllocId]) {
        groups[profAllocId].weekCaptions = Array.from(
          new Set([...groups[profAllocId].weekCaptions, ...weekCaptions]),
        );
      } else {
        groups[profAllocId] = {
          profName,
          weekCaptions: Array.from(new Set(weekCaptions)),
        };
      }
    });
    return Object.values(groups);
  }, [pendingUpdates, allProfessionals, weeks]);

  const persistAllocation = useCallback(
    async ({ professionalAllocationId: key, allocations }) => {
      try {
        if (onUpdatingMargin) onUpdatingMargin();
        await api.patch(
          `${type}/projects/${projectId}/professionals-allocations/${key}/allocations`,
          allocations,
        );
      } catch (err) {
        errorMessage$.next({
          open: true,
          message: err.response && err.response.data.message,
        });
        throw err;
      }
    },
    [onUpdatingMargin, projectId, type],
  );

  const allocationResponse = useCallback(professionalsAllocations => {
    const rows = [];
    const modifiedCells = [];

    professionalsAllocations.map(professionalAllocation => {
      if (!professionalAllocation.AllowEdit) {
        setDisabledRows(state => [...state, professionalAllocation.ID]);
      }

      const row = {
        ID: professionalAllocation.ID,
        Professional: professionalAllocation.Professional,
        JobTitle: professionalAllocation.Position?.Name,
        AllowDelete: professionalAllocation.AllowDelete,
        AllowEdit: professionalAllocation.AllowEdit,
        IsTBD: professionalAllocation.IsTBD,
        HasTutor: professionalAllocation.HasTutor,
        Allocations: [],
      };

      professionalAllocation.Allocations.map(allocation => {
        const projectScheduleWeekId =
          typeof allocation.ProjectScheduleWeek === 'object'
            ? allocation.ProjectScheduleWeek.ID.toString()
            : allocation.ProjectScheduleWeek.toString();
        row[projectScheduleWeekId] = allocation.TotalDays;
        row.Allocations.push({
          ProjectScheduleWeekID: Number(projectScheduleWeekId),
          TotalDays: allocation.TotalDays,
        });

        if (allocation.IsModified) {
          modifiedCells.push({
            rowKey: professionalAllocation.ID,
            columnKey: projectScheduleWeekId,
            previousTotalDays: allocation.PreviousTotalDays,
          });
        }
      });

      rows.push(row);
    });

    setModifiedCells(modifiedCells);
    return rows;
  }, []);

  useEffect(() => {
    if (allProfessionals && allProfessionals.length > 0) {
      const filteredAllocations = allProfessionals
        .filter(
          professional =>
            professional.AllowDelete ||
            professional.Allocations.some(
              allocation => allocation.TotalDays !== 0,
            ),
        )
        .filter(professional => professional.AllowEdit);
      setAllocations(filteredAllocations);
    }
  }, [allProfessionals]);

  const handleEditAllocation = useCallback((key, data) => {
    setAllProfessionals(prevAllocations => {
      const updatedAllocations = prevAllocations.map(allocation => {
        if (allocation.ID === key) {
          Object.keys(data).forEach(projectScheduleWeek => {
            allocation[projectScheduleWeek] = data[projectScheduleWeek];
            const index = allocation.Allocations.findIndex(
              x => x.ProjectScheduleWeekID === Number(projectScheduleWeek),
            );
            if (index !== -1) {
              allocation.Allocations[index].TotalDays =
                data[projectScheduleWeek];
            } else {
              allocation.Allocations.push({
                ProjectScheduleWeekID: Number(projectScheduleWeek),
                TotalDays: data[projectScheduleWeek],
              });
            }
          });
        }
        return allocation;
      });
      return updatedAllocations;
    });

    setPendingUpdates(prev => {
      const prevUpdates = prev[key] || {};
      return {
        ...prev,
        [key]: { ...prevUpdates, ...data },
      };
    });
  }, []);

  const handleBatchSave = useCallback(async () => {
    if (onUpdatingMargin) onUpdatingMargin();
    const updates = Object.entries(pendingUpdates);
    try {
      await Promise.all(
        updates.map(async ([key, data]) => {
          const newAllocations = Object.keys(data).map(projectScheduleWeek => ({
            ProjectScheduleWeek: Number(projectScheduleWeek),
            TotalDays: data[projectScheduleWeek],
          }));
          await persistAllocation({
            professionalAllocationId: key,
            allocations: newAllocations,
          });
        }),
      );
      setPendingUpdates({});
      if (onUpdatedMargin) onUpdatedMargin();
    } catch (err) {}
  }, [pendingUpdates, persistAllocation, onUpdatedMargin, onUpdatingMargin]);

  const scheduleStore = useMemo(() => {
    return new CustomStore({
      key: 'ID',
      loadMode: 'raw',
      load: () => allocations,
      update: (key, data) => {
        scheduleStore.push([
          {
            type: 'update',
            key,
            data,
          },
        ]);
        handleEditAllocation(key, data);
      },
      insert: data => console.log('insert: ', data),
      byKey: (key, data) => console.log(key, data),
    });
  }, [allocations, handleEditAllocation]);

  const handleCloseTooltip = useCallback(() => {
    setTooltip(false);
  }, []);

  const handleDeleteResource = useCallback(
    key => {
      setConflictCells(prevState =>
        prevState.filter(
          conflictCell => Number(conflictCell.rowKey) !== Number(key),
        ),
      );
      scheduleStore.push([{ type: 'remove', key }]);
      if (onUpdatedMargin) onUpdatedMargin();
      onReload();
    },
    [onUpdatedMargin, scheduleStore, onReload],
  );

  useEffect(() => {
    setClosureId('');
    setDisabledRows([]);
  }, [projectId]);

  const loadAllocation = useCallback(async () => {
    setLoading(true);
    if (onUpdatingMargin) onUpdatingMargin();
    setAllocations([]);
    setConflictCells([]);

    let filter = '';
    if (closureId !== '') filter = `?closureId=${closureId}`;

    Promise.all([
      api.get(`${type}/projects/${projectId}/schedule-weeks`),
      api.get(`${type}/projects/${projectId}/conflicts`),
      api.get(
        `${type}/projects/${projectId}/professionals-allocations${filter}`,
      ),
    ])
      .then(([weeks, conflicts, allocations]) => {
        const items = weeks.data.map(item => ({
          ID: item.ID.toString(),
          StartDate: item.ScheduleWeek.WeekStartDate,
          EndDate: item.ScheduleWeek.WeekEndDate,
          WeekNumber: item.ScheduleWeek.WeekNumber,
          Year: item.ScheduleWeek.Year,
          AllowEdit: item.AllowEdit,
          caption: `W${item.ScheduleWeek.WeekNumber}`, // exemplo de caption
        }));

        const conflictCells = [];
        conflicts.data.map(conflict => {
          conflictCells.push({
            rowKey: conflict.ProfessionalAllocation.ID,
            columnKey: conflict.ProjectScheduleWeek.ID,
          });
        });

        setWeeks(items);
        if (closureId === '') setConflictCells(conflictCells);
        setAllProfessionals(allocationResponse(allocations.data));
        if (onUpdatedMargin) onUpdatedMargin(closureId);
        setShowDeallocated(false);
        setLoading(false);
      })
      .catch(error => console.log(error));
  }, [
    allocationResponse,
    closureId,
    onUpdatedMargin,
    onUpdatingMargin,
    projectId,
    setShowDeallocated,
    type,
  ]);

  useEffect(() => {
    loadAllocation();
  }, [loadAllocation, refreshCount]);

  useEffect(() => {
    setTotalConflicts(conflictCells.length);
  }, [conflictCells]);

  const focusOnCurrentWeek = useCallback(() => {
    const { WeekNumber: currentWeekNumber, Year: currentWeekYear } =
      getCurrentWeek();
    const currentWeekObj = weeks.find(
      week =>
        week.WeekNumber === currentWeekNumber && week.Year === currentWeekYear,
    );
    if (currentWeekObj && gridRef.current) {
      const grid = gridRef.current.instance;
      const cell = grid.getCellElement(0, currentWeekObj.ID);
      grid.focus(cell);
    }
  }, [getCurrentWeek, weeks]);

  useEffect(() => {
    setTimeout(focusOnCurrentWeek, 1000);
  }, [focusOnCurrentWeek, weeks]);

  useEffect(() => {
    setAllowEdit(
      _projectStatus !== 'Approved' &&
        _projectStatus !== 'Canceled' &&
        _projectStatus !== 'Closed',
    );
  }, [_projectStatus]);

  function createVisibleProfessionalStore(
    visibleProfessionals,
    handleEditAllocation,
  ) {
    return new CustomStore({
      key: 'ID',
      loadMode: 'raw',
      load: () => visibleProfessionals,
      update: (key, data) => {
        scheduleStore.push([{ type: 'update', key, data }]);
        handleEditAllocation(key, data);
      },
      insert: data => console.log('insert: ', data),
      byKey: (key, data) => console.log(key, data),
    });
  }

  const visibleProfessionalStore = useMemo(
    () =>
      createVisibleProfessionalStore(
        visibleProfessionals,
        handleEditAllocation,
      ),
    [visibleProfessionals, handleEditAllocation],
  );

  useEffect(() => {
    if (showDeallocated) {
      const deallocated = allProfessionals.filter(professional => {
        const hasAllocation = professional.Allocation?.some(
          allocation =>
            professional.AllowEdit === true ||
            (allocation.TotalDays !== 0 && professional.AllowDelete === false),
        );
        const isAlreadyAllocated = allocations.some(
          a => a.ID === professional.ID,
        );
        return !hasAllocation && !isAlreadyAllocated;
      });
      setVisibleProfessionals([
        ...allocations,
        ...deallocated.map(professional => ({
          ...professional,
          AllocationID: null,
          isDeallocated: true,
        })),
      ]);
    } else {
      setVisibleProfessionals([]);
    }
  }, [allProfessionals, allocations, loading, showDeallocated]);

  const handleRowPrepared = e => {
    if (e.data && showDeallocated && e.data.isDeallocated) {
      e.rowElement.classList.add('deallocated-row');
    }
  };

  const isTodayCell = week => {
    const today = new Date();
    return today >= new Date(week.StartDate) && today <= new Date(week.EndDate);
  };

  const cellRender = useCallback(
    e => {
      if (e.rowType === 'data') {
        const isEditable = e.column.allowEditing;
        if (conflictCells.length > 0 || modifiedCells.length > 0) {
          const conflictCellIndex = conflictCells.find(
            conflictCell =>
              Number(conflictCell.rowKey) === Number(e.key) &&
              Number(conflictCell.columnKey) === Number(e.column.dataField), // ✅ Usando dataField
          );
          const modifiedCellIndex = modifiedCells.find(
            modifiedCell =>
              Number(modifiedCell.rowKey) === Number(e.key) &&
              Number(modifiedCell.columnKey) === Number(e.column.dataField), // ✅ Usando dataField
          );
          if (conflictCellIndex && modifiedCellIndex) {
            return (
              <Tooltip
                title={`Previous value: ${modifiedCellIndex.previousTotalDays} and conflicted`}
                placement="top-start"
              >
                <div className="modified-cell">
                  <span className="cell-badge conflict-badge"></span>
                  <span className={!isEditable && 'not-editable-cell'}>
                    {e.value}
                  </span>
                </div>
              </Tooltip>
            );
          }
          if (conflictCellIndex) {
            return (
              <Tooltip title="Conflicted" placement="top-start">
                <div>
                  <span className="cell-badge conflict-badge"></span>
                  <span className={!isEditable && 'not-editable-cell'}>
                    {e.value}
                  </span>
                </div>
              </Tooltip>
            );
          }
          if (modifiedCellIndex) {
            return (
              <Tooltip
                title={`Previous value: ${modifiedCellIndex.previousTotalDays}`}
                placement="top-start"
              >
                <div className="modified-cell">
                  <span>{e.value}</span>
                </div>
              </Tooltip>
            );
          }
        }
        const isToday = weeks.some(
          week => isTodayCell(week) && week.ID === e.column.dataField,
        );
        const cellClassName = isToday ? 'today-cell' : '';
        if (!isEditable) {
          return (
            <div className={`${cellClassName} not-editable-cell`}>
              <span>{e.value}</span>
            </div>
          );
        }
      }
      return e.value;
    },
    [conflictCells, modifiedCells, weeks],
  );

  const handleCellClick = useCallback(
    e => {
      if (
        e.rowType === 'data' &&
        e.column.dataField === 'Professional' &&
        closureId === ''
      ) {
        e.event.stopPropagation();
        setJobTitleId(e.data.JobTitle);
        setAllowDelete(e.data.AllowDelete);
        setProfessionalId(e.data.Professional.ID);
        setProfessionalAllocationId(e.key);
        setIsTBD(e.data.IsTBD);
        setTooltip(true);
      }
    },
    [closureId],
  );

  const handleClickOutside = useCallback(
    event => {
      const tooltipElement = document.querySelector('.resource-dialog');
      const deleteDialog = document.querySelector('.delete-dialog');
      if (
        tooltipElement &&
        !tooltipElement.contains(event.target) &&
        tooltip &&
        !deleteDialog
      ) {
        setTooltip(false);
      }
    },
    [tooltip],
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => {
    unblockRef.current = history.block(tx => {
      if (Object.keys(pendingUpdates).length > 0) {
        setShowUnsavedDialog(true);
        setNextLocation(tx);
        return false;
      }
      return true;
    });
    return () => {
      if (unblockRef.current) {
        unblockRef.current();
      }
    };
  }, [pendingUpdates, history]);

  const handleSaveAndLeave = async () => {
    await handleBatchSave();
    if (unblockRef.current) {
      unblockRef.current();
    }
    setShowUnsavedDialog(false);
    if (nextLocation) {
      history.push(nextLocation.pathname);
    }
  };

  const handleDiscardAndLeave = () => {
    setPendingUpdates({});
    if (unblockRef.current) {
      unblockRef.current();
    }
    setShowUnsavedDialog(false);
    if (nextLocation) {
      history.push(nextLocation.pathname);
    }
  };

  const handleCancelNavigation = () => {
    setShowUnsavedDialog(false);
    setNextLocation(null);
  };

  useEffect(() => {
    const found = userScopes.some(scope => allowEditScopes.includes(scope));
    if (!found) {
      setAllowChangeStatus(false);
      setAllowEdit(false);
    } else {
      const isCoManager = isRestrictedTo(userScopes, [
        schedulingGroups.CoManager,
      ]);
      const isTechLab = isRestrictedTo(userScopes, [schedulingGroups.TechLab]);
      if (isCoManager) {
        const isEditableStatus =
          _projectStatus !== 'Approved' &&
          _projectStatus !== 'Canceled' &&
          _projectStatus !== 'Closed' &&
          _projectStatus !== 'Waiting validation';
        setAllowChangeStatus(
          isEditableStatus &&
            (user.comanagementsIds.includes(_projectComanagement) ||
              (_projectSupportComanagement &&
                user.comanagementsIds.includes(
                  Number(_projectSupportComanagement),
                )) ||
              user.officeId.includes(_officeId)),
        );
        setAllowEdit(
          isEditableStatus &&
            (user.comanagementsIds.includes(_projectComanagement) ||
              (_projectSupportComanagement &&
                user.comanagementsIds.includes(
                  Number(_projectSupportComanagement),
                )) ||
              user.officeId.includes(_officeId)),
        );
      }
      if (isTechLab) {
        setAllowChangeStatus(false);
      }
    }
  }, [
    _projectComanagement,
    _projectStatus,
    _projectSupportComanagement,
    _officeId,
    allowEditScopes,
    user,
    userScopes,
  ]);

  return (
    <>
      {!loading && (
        <div
          style={{
            display: 'flex',
            position: 'relative',
            left: '5rem',
            top: '3.5rem',
            zIndex: '999',
          }}
        >
          <Tooltip title="Save allocations" placement="top" arrow followCursor>
            <Button
              disabled={Object.keys(pendingUpdates).length === 0}
              color="primary"
              onClick={handleBatchSave}
              className="save-button"
            >
              <FiSave size={16} />
            </Button>
          </Tooltip>
          <Tooltip
            title="Go to current week"
            placement="top"
            arrow
            followCursor
          >
            <Button
              className="current-button"
              size="small"
              onClick={focusOnCurrentWeek}
              style={{
                color: '#8b0304',
              }}
            >
              <FiClock size={16} />
            </Button>
          </Tooltip>
        </div>
      )}

      <section id="allocation">
        {loading ? (
          <GridLoaderNew style={{ paddingTop: 30 }} />
        ) : (
          <>
            <DataGrid
              onRowPrepared={handleRowPrepared}
              ref={gridRef}
              scrolling="standard"
              key={allocations.length}
              dataSource={
                showDeallocated ? visibleProfessionalStore : scheduleStore
              }
              showBorders={false}
              remoteOperations
              height="100%"
              showColumnLines
              id="allocation-grid"
              onCellClick={handleCellClick}
              allowColumnResizing={false}
              loadPanel={null}
              onEditingStart={e => {
                if (disabledRows.includes(e.key)) e.cancel = true;
              }}
            >
              <Paging enabled={false} />
              <Editing
                mode="cell"
                allowUpdating={allowEdit}
                selectTextOnEditStart
                startEditAction="click"
              />
              <Column
                dataField="Professional"
                fixed
                allowEditing={false}
                calculateSortValue="Professional.Name"
                defaultSortOrder="asc"
                cellRender={data =>
                  ProfessionaLookup({
                    Name: data.value.Name,
                    JobTitle: {
                      Name: data.data.Position?.Name
                        ? data.data.Position.Name
                        : data.data.JobTitle,
                    },
                    ID: data.value.ID,
                    Login: data.value.Login,
                    IsTBD: data.value.IsTBD,
                    HasClientAgreement: data.value.Skills?.some(
                      x => x.IDSkillType === 6,
                    ),
                    HasTutor: data.data.HasTutor,
                  })
                }
                cssClass="cursor-pointer"
                width={250}
              />
              {weeks.map((week, index) => (
                <Column
                  alignment="center"
                  key={week.ID}
                  dataField={week.ID}
                  caption={week.caption}
                  allowSorting={false}
                  headerCellRender={() => (
                    <WeekHeader
                      isToday={
                        currentWeek === week.WeekNumber &&
                        week.Year === currentYear
                          ? true
                          : !week.AllowEdit
                          ? 'disabled-column'
                          : false
                      }
                      week={week}
                      index={index}
                      isEditable={
                        allowEdit && week.AllowEdit && closureId === ''
                      }
                    />
                  )}
                  width={65}
                  cellRender={cellRender}
                  allowEditing={allowEdit && week.AllowEdit && closureId === ''}
                  cssClass={
                    currentWeek === week.WeekNumber && week.Year === currentYear
                      ? 'highlight-cell'
                      : null
                  }
                >
                  <RangeRule max={5} min={0} message="" />
                </Column>
              ))}
              <Column caption="" />
            </DataGrid>
          </>
        )}
      </section>
      {tooltip && (
        <div className="resource-dialog">
          <DialogResource
            professionalId={professionalId}
            jobTitleId={jobTitleId}
            projectId={projectId}
            professionalAllocationId={professionalAllocationId}
            onResourceDeleted={handleDeleteResource}
            allowEdit={allowEdit}
            allowDelete={allowDelete}
            isTBD={isTBD}
            loadAllocation={loadAllocation}
            onReload={onReload}
            handleCloseTooltip={handleCloseTooltip}
            key={`DialogResource-${isTBD}`}
            type={type}
          />
        </div>
      )}

      <Dialog open={showUnsavedDialog} onClose={handleCancelNavigation}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <p>You have unsaved changes in the following cells:</p>
          <List>
            {groupedUnsavedChanges.map((item, index) => (
              <ListItem key={index}>
                <Paper
                  style={{
                    marginBottom: '8px',
                    padding: '8px',
                    boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
                    width: '100%',
                  }}
                  variant="outlined"
                >
                  <ListItemText
                    primary={`${item.profName} - ${item.weekCaptions.join(
                      ', ',
                    )}`}
                  />
                </Paper>
              </ListItem>
            ))}
          </List>
          <p>Do you want to save these changes before leaving?</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNavigation} color="default">
            Cancel
          </Button>
          <Button onClick={handleDiscardAndLeave} color="secondary">
            Discard Changes
          </Button>
          <Button onClick={handleSaveAndLeave} color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
